import React from 'react';
import '../styles/LegalDocs.css';

const PrivacyPolicy = () => {
  return (
    <div className="legal-doc-container">
      <div className="legal-doc">
        <div className="doc-header">
          <h1>DRIVVE CARPOOLING PLATFORM</h1>
          <h2>PRIVACY POLICY &amp; DATA PROTECTION AGREEMENT</h2>
          <div className="doc-meta">
            <p><strong>Last Updated:</strong> June 11, 2026</p>
            <p><strong>Effective Date:</strong> Immediate upon Download or Account Creation</p>
            <p><strong>Framework:</strong> DPDP Act, 2023 &amp; IT Act, 2000</p>
            <p><strong>Status:</strong> Data Fiduciary (Pending Corporate Registration)</p>
          </div>
        </div>

        <div className="doc-section">
          <h3 className="notice-heading">STATUTORY COMPLIANCE NOTICE:</h3>
          <p>
            This Privacy Policy outlines our systematic practices regarding how user datasets are collected, securely processed, cryptographically stored, and structurally purged under the brand name DRIVVE. This policy explicitly incorporates processing parameters mandated under the Digital Personal Data Protection (DPDP) Act, 2023 (Government of India) and the Information Technology Act, 2000.
          </p>
          <p>
            The founders, promoters, and developers operating under the brand and trade name "DRIVVE" (collectively, the "Platform Owners", "We", "Us", or "Our") attach paramount importance to the security, integrity, and absolute confidentiality of your personal information. This agreement governs our handling of datasets generated when you download, install, configure, or interact with the matching logs of the DRIVVE mobile application.
          </p>
          <p>
            By clicking "I Agree", "Sign Up", or continuing to interact with the Platform, you emit an explicit, informed, specific, unconditional, and unambiguous expression of consent to the processing of your personal data as itemized under this document. If you do not completely agree with these processing pathways, you must immediately halt registration and remove the software build from your hardware device.
          </p>
        </div>

        <div className="doc-section">
          <h3>1. STATUTORY ROLES: DATA FIDUCIARY &amp; DATA PRINCIPALS</h3>
          
          <h4>1.1 Regulatory Classifications</h4>
          <p>
            Under the provisions of the DPDP Act, 2023, the founding Platform Owners of the DRIVVE brand behave legally as a "Data Fiduciary", as we dictate the processing intentions, software logic, and organizational means of handling data. You, as the natural individual accessing the software build, are classified statutorily as a "Data Principal". All processing workflows are governed strictly by the core principles of Lawfulness, Purpose Limitation, and Data Minimization.
          </p>

          <h4>1.2 Future Corporate Succession &amp; Novation</h4>
          <p>
            You explicitly recognize that the Platform Owners are in the process of formalizing an independent corporate structure (such as DRIVVE Private Limited or its equivalent corporate entity). Upon formal corporate registration, the legal status of Data Fiduciary along with all linked databases, encryption records, telemetric archives, and user consent parameters shall automatically assign, transfer, and novate to the newly established corporate entity without requiring prior notices or supplementary digital approvals.
          </p>
        </div>

        <div className="doc-section">
          <h3>2. EXHAUSTIVE CATEGORIES OF DATA PRINCIPAL DATASETS COLLECTED</h3>
          <p>
            To securely execute peer-to-peer route discovery, verify user intent, and run the platform safely, DRIVVE captures the following categories of information:
          </p>
          <ul className="doc-list">
            <li>
              <strong>Core Identity Particulars:</strong> Legal name, personal mobile phone number, independent or corporate verification email address, age/date of birth validation parameters, and profile photograph arrays.
            </li>
            <li>
              <strong>Vehicle &amp; Regulatory Documentation (Car Owners / DRIVVErs Only):</strong> Vehicle manufacturer profile, model name, vehicle type, fuel type, seating capacity, white-plate registration index number, vehicle photographs, physical Registration Certificate (RC) metrics, Driving License ID, and active Pollution Under Control (PUC) certificate indicators.
            </li>
            <li>
              <strong>Live Tracking Location Telemetry:</strong> Highly precise real-time geographic coordinate data streams (GPS latitude, longitude coordinates, direction, and movement velocity parameters) fetched from your device hardware sensors.
            </li>
            <li>
              <strong>Communication Footprints:</strong> Internal text messages exchanged through Platform chat features, customer support interactions, feedback submissions, grievance logs, and survey responses.
            </li>
            <li>
              <strong>System Log &amp; Technical Info:</strong> Device type, model, operating system profiles (Android / iOS builds), background application crash logs, unique device hardware ID tokens, IP addresses, mobile network details, and localized performance analytics.
            </li>
            <li>
              <strong>Usage Information:</strong> Pages viewed, features used, session duration, search patterns, ride interactions, and clickstream parameters.
            </li>
          </ul>
        </div>

        <div className="doc-section">
          <h3>3. PURPOSE LIMITATION AND USE-CASE PROCESSING LOGIC</h3>
          <p>
            DRIVVE processes your personal data strictly for explicit, targeted use-cases linked directly to the technical operation of our peer-to-peer connection tools:
          </p>
          <ul className="doc-list">
            <li>
              <strong>Account Management &amp; Platform Operations:</strong> Setting up and maintaining active user accounts, executing secure authentications, managing user profiles, and providing ongoing technical support.
            </li>
            <li>
              <strong>Peer Matchmaking Execution:</strong> Displaying parallel commuter routes, allowing Passengers to view public profile listings, processing ride requests/acceptances, and facilitating the core manual Request-and-Accept sequence.
            </li>
            <li>
              <strong>Live Tracking Operation:</strong> Feeding real-time coordinates into the active map layers of matched peers during a ride lifecycle, ensuring physical coordination and adding a critical safety layer.
            </li>
            <li>
              <strong>Compliance and Fraud Mitigation:</strong> Cross-examining DRIVVEr credentials, ensuring white-plate regulatory adherence under the Motor Vehicles Act, 1988, executing risk assessments, and automatically throttling robotic data scraping or profile spoofing attempts.
            </li>
            <li>
              <strong>Billing &amp; Escalation Management:</strong> Administering time-sensitive peer cost-contributions and calculating cancellation compensation grids via our secure third-party payment channels.
            </li>
          </ul>
        </div>

        <div className="doc-section">
          <h3>4. DIGITAL KYC HANDSHAKES &amp; ZERO-STORAGE AADHAAR PURGE POLICY</h3>

          <h4>4.1 Institutional Verification Channels</h4>
          <p>
            To ensure community safety, eradicate anonymous profiles, and maintain a trusted network for daily office-goers and commuters, DRIVVE runs digital identity checks by sending verification queries to government-approved structures such as API Setu and DigiLocker systems managed by the National E-Governance Division under the Ministry of Electronics and Information Technology (MeitY).
          </p>

          <h4>4.2 The Immediate Purge Commitment</h4>
          <p>
            In strict compliance with statutory data privacy safeguards, DRIVVE enforces a strict zero-storage commitment regarding your full Aadhaar number, physical card copies, or biometric files. All onboarding checks are funneled through an authorized, licensed Aadhaar User Agency (AUA) interface gateway.
          </p>

          <h4>4.3 Binary Metadata Storage Logic</h4>
          <p>
            Once the government system confirms matching identity profiles, our databases immediately erase the active authentication data from all temporary memory buffers. DRIVVE retains nothing more than a non-sensitive, binary confirmation token (e.g., Status: Verified / User Class: Car Owner / Verification Timestamp) inside our persistent cloud databases to legally prove that a profile has successfully passed compliance vetting.
          </p>
        </div>

        <div className="doc-section">
          <h3>5. LIVE TRACKING TELEMETRY CAPS &amp; DATA MINIMIZATION</h3>

          <h4>5.1 Conditional GPS Capture</h4>
          <p>
            Unlike commercial taxi networks that run continuous background tracking loops, DRIVVE adheres strictly to the principle of data minimization. The Platform requests precise geographic coordinates from your device only during active usage states—specifically when a User is actively querying parallel routes, matching coordinates, or executing a confirmed ride sequence.
          </p>

          <h4>5.2 Complete Telemetry Interruption</h4>
          <p>
            The moment a carpooling ride reaches its destination and the matching users execute final drop-off validation handshakes, all active location telemetry streams are immediately interrupted. DRIVVE completely blocks background geographic capture when you are not actively engaged in a carpooling lifecycle. Disabling location settings completely via device options remains available to users, though it may limit core matchmaking functions.
          </p>
        </div>

        <div className="doc-section">
          <h3>6. DATA SHARING &amp; VIRTUAL NUMBER PROXY PRIVACY MASKING</h3>

          <h4>6.1 Peer Exposure Limitations</h4>
          <p>
            DRIVVE does not monetize, sell, lease, or distribute your personal datasets to third-party marketing companies. Data is exposed to your matched peer only after a valid match is confirmed via the manual Request-and-Accept model. Information shared is restricted entirely to the operational essentials needed to execute the trip (First name or display name, profile photo, vehicle plate, ratings/reviews, and real-time pickup GPS track).
          </p>

          <h4>6.2 Virtual Number Proxy Communication</h4>
          <p>
            To protect your privacy and ensure long-term on-road safety, DRIVVE hides your direct personal mobile phone number from fellow carpoolers. All phone coordination initiated inside the active ride map is funneled through an automated Virtual Number Masking Proxy Service. Matched peers view a temporary, generic platform proxy line, completely shielding your true contact coordinates from exposure.
          </p>

          <h4>6.3 Regulatory and Service Disclosures</h4>
          <p>
            We may share data with authorized technical service providers assisting with cloud hosting, analytics, verification infrastructure, and security services. Furthermore, DRIVVE may disclose information where required by law, such as responding to valid court orders, law enforcement directives, government requests, or to protect user life, property, and community safety during active emergencies.
          </p>
        </div>

        <div className="doc-section">
          <h3>7. CRYPTOGRAPHIC SAFEGUARDS &amp; SECURE TOKEN HANDSHAKES</h3>
          <p>
            DRIVVE implements strict administrative and technological security controls to maintain data security:
          </p>
          <ul className="doc-list">
            <li>
              <strong>Transit Cryptography:</strong> All data communication streams, specifically geographic tracking points, profile particulars, and verification packages, are transmitted over public networks using encrypted Transport Layer Security (TLS/SSL) tunnels to prevent intercept exploits.
            </li>
            <li>
              <strong>Persistent Array Encryption:</strong> Sensitive database records, personal mobile indices, and device log histories archived inside our cloud architecture are structurally separated and protected at rest utilizing AES-256 bit encryption standards.
            </li>
            <li>
              <strong>Secure Token Handshakes:</strong> To eliminate password disclosure vulnerabilities, user account validation and API cross-talk are governed via stateless, short-lived digital token handshakes. These cryptographic signatures grant secure device-level permissions without exposing the primary user account database layer.
            </li>
          </ul>
        </div>

        <div className="doc-section">
          <h3>8. DATA RETENTION PRINCIPLES AND ACCOUNT DELETION</h3>

          <h4>8.1 Retention Thresholds</h4>
          <p>
            DRIVVE retains personal information only for as long as reasonably necessary to fulfill platform operations, provide carpooling services, resolve disputes, prevent fraud, and ensure community safety. Account data is maintained while the user account is active. Safety records, incident reports, and SOS logs may be retained for extended periods to satisfy legal compliance, audit requirements, or active regulatory investigations.
          </p>

          <h4>8.2 Account Deletion and Residual Purging</h4>
          <p>
            Users may request deletion of their account through available platform configurations or by reaching out to support channels. Account deletion processing is subject to the resolution of ongoing active rides or open disputes. Upon execution, personal datasets are structurally purged or anonymized across all production environments within statutory timelines. Residual metadata may be locked in secure storage only if mandated by government tax laws, active police investigation subpoenas, or judicial court orders.
          </p>
        </div>

        <div className="doc-section">
          <h3>9. RIGHTS OF THE DATA PRINCIPAL UNDER REGULATORY LAW</h3>
          <p>
            To fulfill the strict consumer-centric mandates of the PDPP Act, 2023, DRIVVE provides comprehensive in-app controls allowing Data Principals to exercise the following legal rights:
          </p>
          <ol className="doc-list">
            <li>
              <strong>Right to Review and Summary:</strong> The right to receive an explicit summary of what personal information DRIVVE holds in its active databases, along with a list of processing use-cases.
            </li>
            <li>
              <strong>Right to Correction and Completion:</strong> The right to directly update or correct inaccurate mobile entries, update vehicle data parameters, or complete outdated profile fields via your account settings dashboard.
            </li>
            <li>
              <strong>Right to Consent Withdrawal:</strong> The right to revoke data processing consent. Withdrawing consent may cause certain platform features or core ride matches to become technically unavailable.
            </li>
            <li>
              <strong>Right to Nomination:</strong> The right to nominate another individual to exercise data principal rights on your behalf in the event of death or incapacity, in accordance with applicable legal regulations.
            </li>
            <li>
              <strong>Right to Grievance Redressal:</strong> The right to file formal disputes regarding improper data handling with our compliance team before escalating complaints to the Data Protection Board of India.
            </li>
          </ol>
        </div>

        <div className="doc-section">
          <h3>10. CHILDREN'S PRIVACY, COOKIES, AND EXTERNAL LINKS</h3>
          <p>
            The DRIVVE platform is restricted exclusively to individuals who are at least eighteen (18) years of age. DRIVVE does not knowingly collect or process information from minors; if detected, such data will be immediately purged. DRIVVE may use cookies, Software Development Kits (SDKs), pixels, and local storage technologies to remember preferences, analyze performance, and optimize mapping systems. Our separate Cookies Policy governs these tracking parameters. The Platform may contain links to third-party assets; DRIVVE exercises zero control over, and disclaims any responsibility for, the privacy practices or policies of external third-party applications.
          </p>
        </div>

        <div className="doc-section">
          <h3>11. APPLICABILITY &amp; MARKETPLACE ROLLOUT AMENDMENTS</h3>
          <p>
            This Privacy Policy applies uniformly to all platform users, website visitors, and support interactions. DRIVVE is initially deployed via the Google Play Store targeting devices operating on the Android framework, with an expansion to the Apple App Store (iOS) scheduled within three (3) months. This policy holds structural validity across both environments. DRIVVE reserves the right to modify this Privacy Policy as operations scale. Continued use of the platform following published changes constitutes an active acknowledgment of the revised provisions.
          </p>
        </div>

        <div className="doc-section">
          <h3>12. COMPLIANCE REDRESSAL DESK &amp; DATA PROTECTION OFFICER</h3>
          <p>
            To satisfy the statutory mandates established under the Digital Personal Data Protection Act, 2023, DRIVVE hosts a centralized, trackable, and time-bound compliance desk to resolve data complaints, privacy flags, or data privilege requests:
          </p>

          <table className="contact-table">
            <tbody>
              <tr>
                <td><strong>Designated Data Officer</strong></td>
                <td>Promoter Compliance &amp; Data Group (Drivve Brand Owners)</td>
              </tr>
              <tr>
                <td><strong>Primary Privacy Lane</strong></td>
                <td><a href="mailto:privacy@drivve.in">privacy@drivve.in</a></td>
              </tr>
              <tr>
                <td><strong>Compliance Escalations</strong></td>
                <td><a href="mailto:legal@drivve.in">legal@drivve.in</a></td>
              </tr>
              <tr>
                <td><strong>Statutory Settlement Window</strong></td>
                <td>Ticket acknowledgement within 48 hours | Final resolution and closure within 15 days.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;